import { CreditCard, ShieldCheck } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { formatDate } from 'utils/helpers';
import StripeElement from 'components/StripeElement';
import StatusBadge from 'components/StatusBadge';
import { connect } from 'react-redux';
import { BillingActions } from 'store/actions';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { PortalSpinner } from 'components/PortalSpinner';
import { lowerCase, startCase, cloneDeep, set } from 'lodash';
import { chargeStatusHash } from './helper';

let enhancer = connect(null, { ...BillingActions });

const ChargesDetail = ({ showCharge }) => {
  const { id } = useParams();
  const [chargeData, setChargeData] = useState(null)

  let { amount, payment } = chargeData?.attributes || {}
  let { amount: amountWithFee, paid_at, payment_type, state } = payment?.data?.attributes || {}

  useEffect(() => {
    if (id) loadChargeData()
  }, [id]);

  const loadChargeData = async () => {
    try {
      const { data } = await showCharge(id);
      setChargeData(data)
    } catch (error) {
      toast.error(error.errors || 'Failed to load billing data');
    }
  };

  const handlePaymentSuccess = () => {
    setChargeData((prev) => {
      const _chargeDate = cloneDeep(prev);
      set(_chargeDate, 'attributes.payment.data.attributes.state', 'paid');
      set(_chargeDate, 'attributes.payment.data.attributes.paid_at', new Date());
      return _chargeDate;
    });
  };

  if (!chargeData) return <PortalSpinner />

  return (
    <div className="space-y-5">
      <div className="p-4 px-5 rounded-2xl bg-gradient-to-br from-secondary-900 to-slate-800 text-white shadow-md border border-slate-800 space-y-3.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Amount</p>
              <p className="text-sm font-bold text-white mt-0.5">${Number(amount).toFixed(2)}</p>
            </div>
            <div className="h-6 w-px bg-slate-700/80" />
            <div>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Convenience Fee</p>
              <p className="text-sm font-bold text-white mt-0.5">${(amountWithFee - amount).toFixed(2)}</p>
            </div>
          </div>

          <div className="text-right">
            <p className="text-[10px] font-bold text-slate-300 uppercase tracking-wider">Total Due</p>
            <p className="text-xl font-extrabold text-white tracking-tight mt-0.5">${Number(amountWithFee).toFixed(2)}</p>
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-slate-700/70 text-xs font-medium">
          <div className="flex items-center gap-2.5">
            <StatusBadge
              status={startCase(state)}
              className={chargeStatusHash[state]?.badgeClass}
            />
            <div className={`flex items-center gap-1.5 ${chargeStatusHash[state]?.color} font-semibold`}>
              {chargeStatusHash[state]?.icon}
              <span>{chargeStatusHash[state]?.label}</span>
            </div>
          </div>

          {state == 'unpaid' ?
            <div className="text-slate-400 text-right">
              <span>Please complete payment below</span>
            </div>
            :
            <div className="text-slate-400 text-right">
              <span>{formatDate(paid_at, 'short')}</span>
              {payment_type && <span className="ml-1.5 capitalize text-slate-300">• {lowerCase(payment_type)}</span>}
            </div>
          }
        </div>
      </div>

      {state == 'unpaid' &&
        <div>
          <div className="border border-base-200/90 rounded-2xl p-5 shadow-xs bg-base-100 space-y-4">
            <div className="flex items-center gap-2.5 border-b border-base-200/80 pb-3">
              <div className="p-1.5 rounded-lg bg-blue-50 text-blue-500 border border-blue-100">
                <CreditCard size={17} />
              </div>
              <div>
                <h3 className="font-semibold text-secondary-900 text-sm leading-none">Process Payment</h3>
                <p className="text-[11px] text-slate-400 mt-0.5">Select your preferred payment method below</p>
              </div>
            </div>

            <StripeElement
              resource={{ resource_type: 'Charge', resource_id: id }}
              onSuccess={handlePaymentSuccess}
            />
          </div>

          <div className="flex items-center justify-center gap-1.5 text-xs text-slate-400 pt-4 font-medium">
            <ShieldCheck size={14} className="text-emerald-500 shrink-0" />
            <span>Encrypted & Secure Payment</span>
          </div>
        </div>
      }
    </div>
  );
};

export default enhancer(ChargesDetail);
